import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { createPageUrl } from '@/utils';
import { notify } from '@/components/ui/notify';
import {
  fetchAccountCompetitiveProximity,
  fetchCompetitorEntitySignals,
  fetchPersonaBarometer,
} from '@/lib/competitorsApi';
import {
  CompetitorBreakdownTable,
  CompetitorSignalRow,
  ProximityCard,
} from '@/components/competitors/competitorUi';
import { groupSignalsByCompetitor, mergeCompetitorSignals, competitorSignalSourceType } from '@/lib/competitorSignals';
import { useTranslation, translate } from '@/components/utils/translations';

export default function AccountCompetitorsTabContent({
  tenantId,
  accountId,
  personas = [],
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [row, setRow] = useState(null);
  const [linkedPersonas, setLinkedPersonas] = useState([]);
  const [signals, setSignals] = useState([]);

  const personaIdsKey = useMemo(
    () => (personas || []).map((p) => p.person_id || p.id).filter(Boolean).join(','),
    [personas],
  );

  useEffect(() => {
    if (!tenantId || !accountId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    const personIds = personaIdsKey ? personaIdsKey.split(',') : [];

    (async () => {
      try {
        const [proximity, personaBarometer, momentumRaw, ...personaSignalsLists] = await Promise.all([
          fetchAccountCompetitiveProximity(tenantId, accountId, {}),
          fetchPersonaBarometer(tenantId, {}).catch(() => ({ rows: [] })),
          fetchCompetitorEntitySignals(
            'MomentumScore',
            tenantId,
            { account_id: accountId },
            '-date_published',
            200,
          ).catch(() => []),
          ...personIds.map((pid) =>
            fetchCompetitorEntitySignals(
              'PersonaActivity',
              tenantId,
              { person_id: pid },
              '-date_published',
              50,
            ).catch(() => []),
          ),
        ]);

        if (cancelled) return;

        const proximityRow = proximity?.row ?? null;
        setRow(proximityRow);

        const aid = String(accountId);
        setLinkedPersonas(
          (personaBarometer?.rows || []).filter((p) => String(p.account_id) === aid),
        );

        setSignals(mergeCompetitorSignals(momentumRaw, ...personaSignalsLists));
      } catch (e) {
        if (!cancelled) {
          console.warn('Account competitors tab load failed:', e);
          if (e?.status >= 500) {
            notify({
              title: translate('error'),
              description: translate('acctCouldNotLoadCompetitorData'),
              variant: 'destructive',
            });
          }
          setRow(null);
          setSignals([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [tenantId, accountId, personaIdsKey]);

  const competitorGroups = useMemo(() => groupSignalsByCompetitor(signals), [signals]);

  const goToMarket = (competitorName) => {
    const params = new URLSearchParams({ tab: 'competitors' });
    if (competitorName) params.set('competitor', competitorName);
    navigate(createPageUrl(`Market?${params.toString()}`));
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }

  if (!row) return null;

  return (
    <div className="space-y-6">
      <ProximityCard row={row} kind="account" linkedPersonas={linkedPersonas} />

      {competitorGroups.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-[#0A1A2F]">{t('acctCompetitorsAtThisAccount')}</h3>
          <CompetitorBreakdownTable groups={competitorGroups} onRowClick={goToMarket} />
        </div>
      ) : null}

      {signals.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-[#0A1A2F]">{t('acctSignalTimeline')}</h3>
          <div className="space-y-3">
            {signals.map((s) => (
              <CompetitorSignalRow
                key={s.id || `${s.summary}-${s.date_published}`}
                signal={s}
                sourceType={competitorSignalSourceType(s)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
